import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

const domainMeta = [
  { key: "physical", label: "Physical" },
  { key: "emotional", label: "Emotional" },
  { key: "acceptance", label: "Acceptance" },
  { key: "relationships", label: "Relationships" },
  { key: "loving_kindness", label: "Loving-Kindness" },
  { key: "intellectual", label: "Intellectual" },
];

const getScoreColor = (score) => {
  if (score >= 3.5) return "#19B880"; // good
  if (score < 2.5) return "#EF4444"; // poor
  return "#EAB308"; // medium
};

export default function ScoreSummary({ scores, summaries, loading, hasData = true }) {
  const [expanded, setExpanded] = useState(null);

  const handleChange =
    (panelKey) => (_, isExpanded) =>
      setExpanded(isExpanded ? panelKey : null);

  return (
    <Box
      sx={{
        borderRadius: 2,
        bgcolor: "background.paper",
        p: 2.5,
        boxShadow: 1,
        marginBlock: 2
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        PEARLI Score Summary
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        An overview of Participant assessments for each domain
      </Typography>

      {loading ? (
        <>
          <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
        </>
      ) : !hasData ? (
        <Box
          sx={{
            minHeight: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            fontSize: "0.9rem",
            opacity: 0.8,
          }}
        >
          No assessment data available for the selected filters.
        </Box>
      ) : (
        domainMeta.map(({ key, label }) => {
          const score = scores[key];
          const summaryText = summaries?.[key] || "No summary available.";
          const color = getScoreColor(score ?? 0);

          return (
            <Accordion
              key={key}
              expanded={expanded === key}
              onChange={handleChange(key)}
              disableGutters
              sx={{
                "&:not(:last-of-type)": { mb: 1 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <Typography fontWeight={600}>{label}</Typography>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                    <Typography
                      sx={{ color, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                    >
                      {typeof score === "number" ? score.toFixed(1) : "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      / 5.0
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {summaryText}
                </Typography>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
}
